import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cashboxApi, canDoCashbox } from '../../../lib/cashboxApi.js';
import { useRole } from '../../../lib/role.tsx';

export function CashboxShifts() {
  const role = useRole();
  const qc = useQueryClient();
  const canCreate = canDoCashbox(role, 'create');

  const [showOpen, setShowOpen] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [openAmount, setOpenAmount] = useState('0');
  const [closeAmount, setCloseAmount] = useState('');
  const [closeComment, setCloseComment] = useState('');

  const { data: current } = useQuery({
    queryKey: ['cashbox', 'shifts', 'current'],
    queryFn: () => cashboxApi.getCurrentShift(),
  });

  const { data: list } = useQuery({
    queryKey: ['cashbox', 'shifts'],
    queryFn: () => cashboxApi.getShifts(),
  });

  // bug C6: expected cash balance for the open shift, shown in the close modal so
  // the operator sees expected-vs-actual instead of computing the gap by hand.
  const { data: expected } = useQuery({
    queryKey: ['cashbox', 'shifts', 'expected', current?.id],
    queryFn: () => cashboxApi.getShiftExpected(current!.id),
    enabled: showClose && !!current,
  });

  const openShift = useMutation({
    mutationFn: () => cashboxApi.openShift({ openingAmount: parseFloat(openAmount) || 0 }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cashbox'] });
      setShowOpen(false);
    },
  });

  const closeShift = useMutation({
    mutationFn: () =>
      cashboxApi.closeShift(current!.id, {
        closingAmount: parseFloat(closeAmount) || 0,
        comment: closeComment || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cashbox'] });
      setShowClose(false);
    },
  });

  const shifts = list?.items ?? [];

  const discrepancyBadge = (d: number | null) => {
    if (d === null) return <span className="muted">—</span>;
    if (d === 0) return <span className="badge-a green">0</span>;
    return (
      <span className={`badge-a ${Math.abs(d) > 50 ? 'red' : 'yellow'}`}>
        {d > 0 ? '+' : ''}{Math.round(d).toLocaleString('ru-RU')} GEL
      </span>
    );
  };

  return (
    <div>
      {/* Current shift banner */}
      <div className={`cb-shift-banner ${current ? 'open' : 'closed'}`} data-testid="shifts-banner">
        {current ? (
          <>
            <span className="badge-a green">Смена открыта</span>
            <span className="muted" style={{ fontSize: 12 }}>
              {current.id} · с {new Date(current.openedAt).toLocaleString('ru-RU')}
            </span>
            {canCreate && (
              <button className="btn-admin" onClick={() => setShowClose(true)} style={{ marginLeft: 'auto' }}>
                Закрыть смену
              </button>
            )}
          </>
        ) : (
          <>
            <span className="muted">Смена не открыта</span>
            {canCreate && (
              <button className="btn-admin primary" onClick={() => setShowOpen(true)} style={{ marginLeft: 'auto' }}>
                Открыть смену
              </button>
            )}
          </>
        )}
      </div>

      {/* Shifts table */}
      <div className="panel">
        <div className="panel-head">
          <p className="panel-title">История смен</p>
        </div>
        <table className="data" data-testid="shifts-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Открыта</th>
              <th>Закрыта</th>
              <th className="num">Открытие GEL</th>
              <th className="num">Закрытие GEL</th>
              <th className="num">Ожидалось GEL</th>
              <th>Расхождение</th>
            </tr>
          </thead>
          <tbody>
            {shifts.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: 'var(--mnp-fg-3)' }}>
                  Смен пока нет
                </td>
              </tr>
            )}
            {shifts.map((s) => (
              <tr key={s.id}>
                <td className="mono" style={{ fontSize: 11 }}>{s.id}</td>
                <td>{new Date(s.openedAt).toLocaleString('ru-RU')}</td>
                <td>{s.closedAt ? new Date(s.closedAt).toLocaleString('ru-RU') : <span className="badge-a green">Открыта</span>}</td>
                <td className="num tnum">{Math.round(s.openingAmount).toLocaleString('ru-RU')}</td>
                <td className="num tnum">{s.closingAmount !== null ? Math.round(s.closingAmount).toLocaleString('ru-RU') : '—'}</td>
                <td className="num tnum">{s.expectedAmount !== null ? Math.round(s.expectedAmount).toLocaleString('ru-RU') : '—'}</td>
                <td>{discrepancyBadge(s.discrepancy)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Open shift modal */}
      {showOpen && (
        <div className="cb-overlay" data-testid="open-shift-modal">
          <div className="cb-modal">
            <p className="cb-modal-title">Открыть смену</p>
            <div className="cb-field">
              <label>Начальный остаток (GEL)</label>
              <input className="input-admin" type="number" min="0" step="0.01" value={openAmount} onChange={(e) => setOpenAmount(e.target.value)} />
            </div>
            {openShift.isError && <p style={{ color: 'var(--mnp-red-dark)', fontSize: 13 }}>Ошибка при открытии</p>}
            <div className="cb-modal-actions">
              <button className="btn-admin" onClick={() => setShowOpen(false)}>Отмена</button>
              <button className="btn-admin primary" onClick={() => openShift.mutate()} disabled={openShift.isPending}>
                {openShift.isPending ? 'Открываю…' : 'Открыть'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close shift modal */}
      {showClose && current && (
        <div className="cb-overlay" data-testid="close-shift-modal">
          <div className="cb-modal">
            <p className="cb-modal-title">Закрыть смену</p>
            {expected && (
              <div className="cb-field" data-testid="close-expected">
                <label>Ожидаемый остаток (касса)</label>
                <p className="muted" style={{ margin: '4px 0', fontSize: 13 }}>
                  открытие {Math.round(expected.openingAmount).toLocaleString('ru-RU')}
                  {' + приход '}{Math.round(expected.cashIncome).toLocaleString('ru-RU')}
                  {' − расход '}{Math.round(expected.cashExpense).toLocaleString('ru-RU')}
                  {' = '}
                  <b>{Math.round(expected.expectedAmount).toLocaleString('ru-RU')} GEL</b>
                </p>
              </div>
            )}
            <div className="cb-field">
              <label>Фактический остаток (GEL)</label>
              <input className="input-admin" type="number" min="0" step="0.01" value={closeAmount} onChange={(e) => setCloseAmount(e.target.value)} />
              {expected && closeAmount.trim() !== '' && Number.isFinite(parseFloat(closeAmount)) && (
                <p style={{ margin: '6px 0 0', fontSize: 13 }} data-testid="close-discrepancy">
                  Расхождение:{' '}
                  {discrepancyBadge(Math.round((parseFloat(closeAmount) - expected.expectedAmount) * 100) / 100)}
                </p>
              )}
            </div>
            <div className="cb-field">
              <label>Примечание</label>
              <input className="input-admin" value={closeComment} onChange={(e) => setCloseComment(e.target.value)} />
            </div>
            {closeShift.isError && <p style={{ color: 'var(--mnp-red-dark)', fontSize: 13 }}>Ошибка при закрытии</p>}
            <div className="cb-modal-actions">
              <button className="btn-admin" onClick={() => setShowClose(false)}>Отмена</button>
              <button className="btn-admin primary" onClick={() => closeShift.mutate()} disabled={closeShift.isPending}>
                {closeShift.isPending ? 'Закрываю…' : 'Закрыть смену'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
